// src/pages/categories/CategoryForm.js

import React, { useState, useEffect } from 'react';
import useApi from '../../hooks/useApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';

const CategoryForm = ({ category, onClose, onSaveSuccess }) => {
  const api = useApi();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentCategoryId: '', // New state for parent category
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allCategories, setAllCategories] = useState([]); // To store all categories for the dropdown

  useEffect(() => {

     console.log("@@@ category.parentCategoryId @@@",category);
    // Fetch all categories for the parent category dropdown
    const fetchAllCategories = async () => {
      try {
        const response = await api.get('categories', { queryParams: { limit: 1000 } }); // Fetch a large enough list or adjust limit for all
        setAllCategories(response.data || response); // Adjust based on your backend response structure
      } catch (err) {
        console.error("Failed to fetch all categories for parent dropdown:", err);
        setError("Failed to load categories for parent selection. " + (err.message || ""));
      }
    };
    fetchAllCategories();

    // Populate form data if editing an existing category
    if (category) {
     
      
      setIsEditing(true);
      setFormData({
        name: category.name || '',
        description: category.description || '',
        parentCategoryId: category.parentCategoryId || '', // Pre-fill if exists
      });
    }
  }, [category, api]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Basic validation
    if (!formData.name.trim()) {
      setError("Category Name is required.");
      setLoading(false);
      return;
    }

    // Prevent a category from being its own parent
    if (isEditing && formData.parentCategoryId === category._id) {
      setError("A category cannot be its own parent category.");
      setLoading(false);
      return;
    }

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        // Only include parentCategoryId if a value is selected, otherwise send null or omit
        parentCategoryId: formData.parentCategoryId || null,
      };

      if (isEditing) {
        await api.put(`categories/${category._id}`, payload);
        alert("Category updated successfully!");
      } else {
        await api.post('categories', payload);
        alert("Category created successfully!");
      }
      onSaveSuccess(); // Notify parent (GenericListPage) to refresh data
      onClose(); // Close the modal
    } catch (err) {
      setError(err.message || "Operation failed.");
      console.error("Category form submission error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filter out the current category itself from the parent category dropdown if editing
  const filteredCategories = allCategories.filter(cat => cat._id !== category?._id);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {loading && <LoadingSpinner />}
      {error && <ErrorMessage message={error} />}

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">Category Name</label>
        <input
          type="text"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
        />
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description (Optional)</label>
        <textarea
          name="description"
          id="description"
          value={formData.description}
          onChange={handleChange}
          rows="3"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
        ></textarea>
      </div>

      {/* New: Parent Category Dropdown */}
      <div>
        <label htmlFor="parentCategoryId" className="block text-sm font-medium text-gray-700">Parent Category (Optional)</label>
        <select
          name="parentCategoryId"
          id="parentCategoryId"
          value={formData.parentCategoryId}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
        >
          <option value="">No Parent Category</option> {/* Option to select no parent */}
          {filteredCategories.map((cat) => (
            <option key={cat._id} value={cat._id}>{cat.name}</option>
          ))}
        </select>
      </div>

      <div className="flex justify-end space-x-3 mt-6">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {loading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Category')}
        </button>
      </div>
    </form>
  );
};

export default CategoryForm;
